import { useCallback } from "react";

import { IconPlus, IconRefresh } from "@tabler/icons";
import { useTheme } from "styled-components";

import { useGetOrganisationsQuery } from "@sso/apis/organisations/hooks";
import { TableHeader } from "@sso/components/TableHeader";
import { TableLayoutWrapper } from "@sso/components/TableLayoutWrapper";
import { TablePagination } from "@sso/components/TablePagination";
import { Dialog } from "@sso/constant";
import { TableProvider, TableProviderState } from "@sso/context/tableContext";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import { selectOrganisationPagination } from "@sso/store/slices/organisation";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";

import { OrganisationsTable } from "./OrganisationsTable";
import { organisationTableColumns } from "./organisationTableColumns";
import { OrganisationsTableMerge } from "./OrganisationTableMerge";

export const Organisations = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const orgPagination = useAppSelector(selectOrganisationPagination);

  const { data, isFetching, error, refetch } = useGetOrganisationsQuery();

  const tableContextInstance: TableProviderState = { ...orgPagination, columns: organisationTableColumns };

  const handleAddOrganisation = useCallback(() => dispatch(openDialog({ type: Dialog.ADD_ORGANISATION })), [dispatch]);

  const TableActions = (
    <Flex>
      <OrganisationsTableMerge />
      <IconButton title="Refresh" onClick={refetch}>
        <IconRefresh />
      </IconButton>
      <IconButton title="Add Organisations" onClick={handleAddOrganisation}>
        <IconPlus />
      </IconButton>
    </Flex>
  );

  return (
    <>
      {isFetching && <CircleLoading color={theme.colors.secondary.dark} size={8} blurBackground />}
      <TableProvider tableInstance={tableContextInstance}>
        <TableHeader title="Organisations" TableActions={TableActions} />
        <TableLayoutWrapper error={error} data={data}>
          {data && <OrganisationsTable orgList={data.resources} />}
        </TableLayoutWrapper>
        <TablePagination totalPages={data?.totalPages} />
      </TableProvider>
    </>
  );
};
